import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import { createDebug } from '../../utils/debugLog'
import { supabase } from '../../utils/supabase'
import Modal from '../ui/Modal'
import { softDeleteAccount } from '../../api/auth'
import { emailConfirmationMatches } from './emailConfirmationMatch'

const log = createDebug('dw:account-self-service:delete-account-modal')

interface DeleteAccountModalProps {
  isOpen: boolean
  onClose: () => void
  email: string
}

export default function DeleteAccountModal({
  isOpen,
  onClose,
  email,
}: DeleteAccountModalProps) {
  const navigate = useNavigate()
  const { t } = useTranslation('auth')
  const [confirmEmail, setConfirmEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      // Don't clear the typed email between open/close cycles per UX:
      // the architect spec says "failure mode keeps the email typed".
      setError(null)
      setSubmitting(false)
    } else {
      log('open')
    }
  }, [isOpen])

  const emailMatches = emailConfirmationMatches(confirmEmail, email)
  const formValid = emailMatches

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formValid || submitting) return
    setSubmitting(true)
    setError(null)
    log('submit start')
    try {
      await softDeleteAccount(confirmEmail)
      log('submit success')
      // Sign out before navigating so the next page sees a fresh session.
      await supabase.auth.signOut()
      navigate('/')
    } catch (err) {
      const message = err instanceof Error ? err.message : t('failedToDeleteAccount')
      log('submit error message=%s', message)
      setError(message)
      setSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" title={t('deleteAccount')}>
      <form
        className="flex flex-col gap-3"
        onSubmit={handleSubmit}
        data-testid="delete-account-modal"
      >
        <div className="flex flex-col gap-2 p-3 rounded-md bg-red-500/5 border border-red-500/30">
          <p className="text-sm text-red-300 font-medium">
            {t('deleteAccountWarning')}
          </p>
          <p className="text-xs font-medium text-muted-foreground">
            {t('deleteAccountDetails')}
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <label
            htmlFor="confirm-email"
            className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
          >
            <Trans
              i18nKey="typeEmailToConfirm"
              t={t}
              values={{ email }}
              components={{
                emailSpan: <span className="font-mono text-foreground" />,
              }}
            />
          </label>
          <input
            id="confirm-email"
            data-testid="delete-confirm-email-input"
            type="email"
            autoComplete="off"
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            className="px-3 py-2 rounded-md bg-surface-2 border border-border text-sm text-foreground"
            required
          />
        </div>
        {error && (
          <div className="text-xs font-medium text-red-400" data-testid="delete-account-error">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 mt-1">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-2 rounded-md text-sm bg-surface-2 hover:bg-surface-3 text-foreground disabled:opacity-50 transition-colors cursor-pointer"
          >
            {t('cancel')}
          </button>
          <button
            type="submit"
            data-testid="delete-account-submit"
            disabled={!formValid || submitting}
            className="px-3 py-2 rounded-md text-sm bg-red-500 hover:bg-red-600 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
          >
            {submitting ? t('deleting') : t('deleteAccount')}
          </button>
        </div>
      </form>
    </Modal>
  )
}
